import download from "downloadjs";
import RestClientService from "@/core/services/restClient.service";
import { ICoreResponse } from "@/core/interfaces/coreResponse.interface";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { IMyBookingsFlight } from "@/core/interfaces/state/flightBookings.interface";
import { IMyBookingsTour } from "@/core/interfaces/state/tourBookings.interface";
import { MyBookingsResponseModel } from "@/modules/MyBookings/models/myBookings.response";
import { MY_BOOKINGS_API_PATH } from "@/modules/MyBookings/constants";
import { FetchBookingsParamsRequestModel } from "@/modules/MyBookings/models/fetchBookingsParams.request";
import { formatString } from "@/core/utils/helpers";
import { SendAirlineTicketBodyRequest } from "@/modules/MyBookings/models/sendAirlineTicketBody.request";
import { IMyBookingsHotel } from "@/core/interfaces/state/hotelBookings.interface";
import { CACHE_NAMES } from "@/core/constants";
import { parseWallClock } from "@/core/utils/pnrExpiry";

function toRequiredDate(value?: string | null): Date {
  return new Date(value ?? "");
}

function toOptionalDate(value?: string | null): Date | null {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function mapFlightBookingResponse(
  data: MyBookingsResponseModel,
): IMyBookingsFlight {
  return {
    count: data.count,
    next: data.next,
    previous: data.previous,
    bookings: data.results.map((item) => ({
      id: item.booking_id,
      booking_actions: item.booking_actions,
      bookingId: item.booking_id,
      bookingTime: item.booking_time,
      travelDate: item.flight_details?.travel_dates[0] || "",
      status: item.status,
      status_remarks: item.status,
      travelers: item.flight_details?.passengers_names || [],
      sector: item.flight_details?.sector || "",
      pnr: item.flight_details?.supplier_pnr || "",
      supplier: item.flight_details?.supplier || "",
      user_email: item.organization_user?.email || "-",
      created_by: item.created_by || null,
      // Expiry is read as a plain wall-clock string (offset ignored).
      initiatedBookingExpiresAt:
        parseWallClock(item.initiated_booking_expires_at) ?? new Date(NaN),
      confirmedBookingExpiresAt: parseWallClock(
        item.confirmed_booking_expires_at,
      ),
    })),
  };
}

function mapHotelBookingResponse(
  data: MyBookingsResponseModel,
): IMyBookingsHotel {
  return {
    count: data.count,
    next: data.next,
    previous: data.previous,
    bookings: data.results.map((item) => ({
      id: item.booking_id,
      bookingId: item.booking_id,
      bookingTime: item.booking_time,
      hotelName: item.hotel_details?.property?.name || "",
      hotelCity: item.hotel_details?.property?.city || "",
      hotelCountry: item.hotel_details?.property?.country || "",
      hotelProvider: item.hotel_details?.hotel_provider || "",
      guests: item.hotel_details?.guests || [],
      status: item.status,
      checkOut: item.hotel_details?.checkout || "",
      checkIn: item.hotel_details?.checkin || "",
      organization_user: item.organization_user || null,
      hcn: item.hotel_details?.hcn || "",
      initiatedBookingExpiresAt: toRequiredDate(
        item.initiated_booking_expires_at,
      ),
      confirmedBookingExpiresAt: toOptionalDate(
        item.confirmed_booking_expires_at,
      ),
      created_by: item.created_by || null,
    })),
  };
}

function mapTourBookingResponse(
  data: MyBookingsResponseModel,
): IMyBookingsTour {
  return {
    count: data.count,
    next: data.next,
    previous: data.previous,
    bookings: (data.results ?? []).map((item) => {
      const isUSD = item.tour_details.base_currency === "USD";

      return {
        id: item.booking_id,
        bookingId: item.booking_id,
        title: item.tour_details.title as string,
        passportRequired: item.tour_details?.passport_required,
        guests: item.tour_details?.number_of_guest,
        bookingTime: item.booking_time,
        travelDate: item.tour_details?.start_date || "",
        currency: isUSD
          ? item.tour_details.base_currency || "PKR"
          : item.tour_details.equivalent_currency || "PKR",
        price: isUSD
          ? Number(item.tour_details?.total_fare)
          : Number(item.tour_details.total_equivalent_fare),
        status: item.status,
        travelers: item.tour_details.passenger_list || [],
        tourType: item.tour_details?.tour_type,
        endDate: item.tour_details?.end_date,
        startDate: item.tour_details?.start_date,
        agent: item.organization_user?.email || "",
        organization_user: item.organization_user || null,
        created_by: item.created_by || null,
        initiatedBookingExpiresAt: toRequiredDate(
          item.initiated_booking_expires_at,
        ),
        confirmedBookingExpiresAt: toOptionalDate(
          item.confirmed_booking_expires_at,
        ),
      };
    }),
  };
}

class MyBookingsService {
  private restClient: RestClientService;

  constructor() {
    this.restClient = new RestClientService();
  }

  public async fetchIssuanceLogs(pnr: string): Promise<any> {
    try {
      const response: ICoreResponse = await this.restClient.get(
        MY_BOOKINGS_API_PATH.ISSUEANCE_LOGS + pnr + "/",
      );

      if (response.data && response.success) {
        return response.data;
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (err) {
      console.error("Error in fetchIssuanceLogs:", err);
      throw err;
    }
  }

  public async fetchBookingLogs(id: string): Promise<any> {
    try {
      const response: ICoreResponse = await this.restClient.get(
        MY_BOOKINGS_API_PATH.BOOKING_LOGS + "/" + id + "/",
      );

      if (response.data && response.success) {
        return response.data;
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (err) {
      console.error("Error in fetchBookingLogs:", err);
      throw err;
    }
  }
  public downloadVoucher(bookingId: string): Promise<ICoreResponse> {
    return this.restClient.get(
      `${MY_BOOKINGS_API_PATH.DOWNLOAD_VOUCHER}/${bookingId}`,
      {},
      { responseType: "blob" },
    );
  }
  public async fetchFlightBookings(
    payload: FetchBookingsParamsRequestModel,
    isFetchNewData: boolean,
  ): Promise<IMyBookingsFlight> {
    let mappedFlightBookings: IMyBookingsFlight = {
      count: 0,
      next: "",
      previous: "",
      bookings: [],
    };
    try {
      if (isFetchNewData) {
        await caches.delete(CACHE_NAMES.flightsBookings);
      }
      const response: ICoreResponse<MyBookingsResponseModel> =
        await this.restClient.get(MY_BOOKINGS_API_PATH.GET_BOOKINGS, payload);
      if (response.success && response.data) {
        mappedFlightBookings = mapFlightBookingResponse(response.data);
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (err) {
      console.error("Error in fetchFlightBookings:", err);
    }
    return mappedFlightBookings;
  }

  public async fetchTourBookings(
    payload: FetchBookingsParamsRequestModel,
  ): Promise<IMyBookingsTour> {
    let mappedTourBookings: IMyBookingsTour = {
      count: 0,
      next: "",
      previous: "",
      bookings: [],
    };
    try {
      const response: ICoreResponse<MyBookingsResponseModel> =
        await this.restClient.get(MY_BOOKINGS_API_PATH.GET_BOOKINGS, payload);
      if (response.success && response.data) {
        mappedTourBookings = mapTourBookingResponse(response.data);
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (err) {
      console.error("Error in fetchTourBookings:", err);
    }
    return mappedTourBookings;
  }

  public async fetchHotelBookings(
    params: FetchBookingsParamsRequestModel,
  ): Promise<IMyBookingsHotel> {
    let mappedHotelBookings: IMyBookingsHotel = {
      count: 0,
      next: "",
      previous: "",
      bookings: [],
    };
    try {
      const response: ICoreResponse<MyBookingsResponseModel> =
        await this.restClient.get(MY_BOOKINGS_API_PATH.GET_BOOKINGS, params);
      if (response.success && response.data) {
        mappedHotelBookings = mapHotelBookingResponse(response.data);
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (err) {
      console.error("Error in fetchHotelBookings:", err);
    }
    return mappedHotelBookings;
  }

  public async dowloadBrandTicket(bookingId: string, pnr: string) {
    const response: ICoreResponse = await this.restClient.get(
      formatString(MY_BOOKINGS_API_PATH.DOWNLOAD_BRAND_TICKET, bookingId),
      {},
      { responseType: "blob" },
    );

    if (!response.success || !response.data) {
      throw response;
    }

    const fileName = pnr + "-E-ticket.pdf";
    download(response.data, fileName);
  }
  public async downloadTourVoucher(bookingId: string) {
    try {
      const response: ICoreResponse = await this.restClient.get(
        formatString(MY_BOOKINGS_API_PATH.DOWNLOAD_TOUR_VOUCHER, bookingId),
        {},
        { responseType: "blob" },
      );
      download(response.data, "voucher.pdf");
    } catch (err) {
      console.error("Error in downloadTourVoucher:", err);
    }
  }
  public async sendAirlineTicket(payload: SendAirlineTicketBodyRequest) {
    try {
      const response: ICoreResponse = await this.restClient.post(
        MY_BOOKINGS_API_PATH.SEND_AIRLINE_TICKET,
        payload,
      );

      if (response.success) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          NOTIFICATION_MESSAGES.AIRLINE_TICKETS_SENT_SUCCESS;
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
      }
    } catch (err) {
      console.error("Error in sendAirlineTicket:", err);
    }
    notificationService.triggerNotification();
  }
}

export default MyBookingsService;
